// src/components/UsersList.jsx
import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../utils/axios";
import toast from "react-hot-toast";
import {
  FaEdit,
  FaTrash,
  FaEye,
  FaTimes,
  FaUser,
  FaPhone,
  FaMapMarkerAlt,
  FaPlusCircle,
  FaIdCard,
  FaSearch,
} from "react-icons/fa";

// ✅ Sheet JSON (Images + Docs live here)
const SHEET_JSON_URL =
  "https://script.google.com/macros/s/AKfycbxaCYZ_n4xBjbf1jFGbNLrQhkmzyaCacHAbKdW9fLa_paVGMXAUKsh55hBPtfbrfATXsw/exec";

// ✅ Detect Drive "view" link (HTML page)
const isDriveViewLink = (url) =>
  /drive\.google\.com\/file\/d\/[^/]+\/view/i.test(String(url || ""));

// ✅ For embedding Drive without "direct/convert": use /preview in iframe
const driveViewToPreview = (url) =>
  String(url || "").replace("/view", "/preview");

export default function UsersList({ onEditUser, onAddSale }) {
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);

  // ✅ Separate loading flags (prevents % jump)
  const [usersLoading, setUsersLoading] = useState(true);
  const [sheetLoading, setSheetLoading] = useState(true);
  const loading = usersLoading || sheetLoading;
  const sheetLoaded = !sheetLoading;

  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [search, setSearch] = useState("");
  const [quickUser, setQuickUser] = useState(null);

  // ✅ Sheet map by mobile (only for image/docs)
  const [sheetByMobile, setSheetByMobile] = useState({});

  // ✅ Pagination
  const [page, setPage] = useState(1);
  const rowsPerPage = 8;

  const normalizeMobile = (v) =>
    String(v || "")
      .replace(/\D/g, "")
      .slice(-10);

  const buildSheetMap = (arr) => {
    const out = {};
    (Array.isArray(arr) ? arr : []).forEach((row) => {
      const mob = normalizeMobile(row?.MOBILE);
      if (!mob) return;
      out[mob] = row;
    });
    return out;
  };

  const fetchSheetImages = useCallback(async () => {
    setSheetLoading(true);
    try {
      const res = await fetch(SHEET_JSON_URL, { method: "GET" });
      if (!res.ok) throw new Error("Sheet JSON failed: " + res.status);
      const raw = await res.json();
      setSheetByMobile(buildSheetMap(raw));
    } catch (e) {
      console.error("Sheet JSON fetch error:", e);
      setSheetByMobile({});
    } finally {
      setSheetLoading(false);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const dealer = JSON.parse(localStorage.getItem("dealer"));
      if (!dealer?.mobile) {
        toast.error("Session expired. Please login again.");
        navigate("/");
        return;
      }

      const res = await api.get("/users", {
        headers: { "x-auth-phone": dealer.mobile },
      });

      const list = res.data.users || [];
      setUsers(list);
      setPage(1);
    } catch (err) {
      toast.error(err.response?.data?.message || "");
      console.error(err);
    } finally {
      setUsersLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchUsers();
    fetchSheetImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ IMPORTANT: Ignore API image/docs completely. Only use sheet urls (as-is)
  const resolveUser = useCallback(
    (u) => {
      const mob = normalizeMobile(u?.mobile);
      const sheet = mob ? sheetByMobile[mob] : null;

      const rawProfile =
        sheet?.["PROFILE PIC"] ||
        sheet?.["PROFILE PIC "] ||
        sheet?.PROFILE_PIC ||
        "";
      const rawPan = sheet?.PAN || "";
      const rawAadhar = sheet?.AADHAR || "";

      return {
        ...u,
        profilePic: String(rawProfile || "").trim() || null,
        documents: {
          ...(u?.documents || {}),
          pan: String(rawPan || "").trim() || null,
          aadhar: String(rawAadhar || "").trim() || null,
        },
      };
    },
    [sheetByMobile],
  );

  // ✅ Set default selectedUser ONLY after both API+Sheet are ready (prevents flicker)
  useEffect(() => {
    if (!loading && users.length > 0) {
      setSelectedUser(resolveUser(users[0]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, users, sheetByMobile]);

  const getProfileCompletion = (user) => {
    // ✅ Prevent initial API-only % from showing
    if (!sheetLoaded) return { percent: 0, color: "red", pending: true };

    const ru = resolveUser(user);

    const requiredFields = [
      ru.name,
      ru.mobile,
      ru.type,
      ru.area,
      ru.profilePic,
      ru.documents?.pan,
      ru.documents?.aadhar,
    ];

    const filled = requiredFields.filter(Boolean).length;
    const total = requiredFields.length;

    const percent = Math.round((filled / total) * 100);

    let color = "red";
    if (percent >= 80) color = "green";
    else if (percent >= 50) color = "yellow";

    return { percent, color, pending: false };
  };

  const CircularProfile = ({ percent, color, image, name, pending }) => {
    const radius = 22;
    const stroke = 4;
    const r = radius - stroke;
    const c = 2 * Math.PI * r;
    const offset = c - (percent / 100) * c;

    const colors = { green: "#22c55e", yellow: "#eab308", red: "#ef4444" };

    return (
      <div className="flex flex-col items-center gap-1">
        <div className="relative w-14 h-14">
          <svg
            width="56"
            height="56"
            className="absolute top-0 left-0 -rotate-90"
          >
            <circle
              stroke="#e5e7eb"
              fill="none"
              strokeWidth={stroke}
              r={r}
              cx="28"
              cy="28"
            />
            <circle
              stroke={colors[color]}
              fill="none"
              strokeWidth={stroke}
              strokeLinecap="round"
              strokeDasharray={`${c} ${c}`}
              strokeDashoffset={offset}
              r={r}
              cx="28"
              cy="28"
            />
          </svg>

          {/* ✅ If it's Drive /view, it won't render in <img>. Use iframe preview. */}
          {image ? (
            isDriveViewLink(image) ? (
              <iframe
                src={driveViewToPreview(image)}
                title="profile-preview"
                className="w-10 h-10 rounded-full absolute top-1/2 left-1/2
                         -translate-x-1/2 -translate-y-1/2 bg-white border"
                style={{ pointerEvents: "none" }}
                allow="autoplay"
              />
            ) : (
              <img
                src={image}
                alt={name}
                className="w-10 h-10 rounded-full object-cover absolute top-1/2 left-1/2
                         -translate-x-1/2 -translate-y-1/2 bg-white"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            )
          ) : (
            <div
              className="w-10 h-10 rounded-full bg-yellow-200 flex items-center justify-center
                       font-bold text-yellow-800 absolute top-1/2 left-1/2
                       -translate-x-1/2 -translate-y-1/2"
            >
              {name?.charAt(0)?.toUpperCase() || "U"}
            </div>
          )}
        </div>

        <span
          className={`text-xs font-bold ${
            color === "green"
              ? "text-green-600"
              : color === "yellow"
                ? "text-yellow-600"
                : "text-red-600"
          }`}
        >
          {pending ? "--" : `${percent}%`}
        </span>
      </div>
    );
  };

  const filteredUsers = useMemo(() => {
    if (!search.trim()) return users;
    const q = search.toLowerCase();
    return users.filter((u) =>
      [u.name, u.mobile, u.type, u.district, u.area]
        .filter(Boolean)
        .some((field) => field.toString().toLowerCase().includes(q)),
    );
  }, [users, search]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / rowsPerPage));
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const currentRows = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return filteredUsers.slice(start, start + rowsPerPage);
  }, [filteredUsers, page]);

  const handleDelete = (userId, userName) => {
    toast(
      (t) => (
        <div className="max-w-sm w-full">
          <p className="text-gray-800 font-medium mb-4">
            Are you sure you want to delete <strong>{userName}</strong>?
          </p>
          <div className="flex gap-3 justify-end">
            <button
              onClick={() => toast.dismiss(t.id)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition"
            >
              Cancel
            </button>
            <button
              // ✅ UPDATED: inside your "Yes, Delete" onClick
              // It will (1) submit mobile to Google Form, and (2) call your API delete.
              // Both run together (parallel). API result still controls UI removal.

              onClick={async () => {
                toast.dismiss(t.id);
                const loadingToast = toast.loading("Deleting user...");

                try {
                  const dealer = JSON.parse(
                    localStorage.getItem("dealer") || "{}",
                  );

                  // ✅ Find the user's mobile (from current users list) using userId
                  const u = users.find((x) => x._id === userId);
                  const mobile10 = String(u?.mobile || "")
                    .replace(/\D/g, "")
                    .slice(-10);

                  // ✅ Google Form submit endpoint (use formResponse, not viewform)
                  const formUrl =
                    "https://docs.google.com/forms/d/e/1FAIpQLSeFrj5ytWdqfoq8Xk_9UBXpNNxuKJHFaT0XEw0TzEzhPNwU6Q/formResponse?";

                  // ✅ Querystring payload
                  const formBody = new URLSearchParams();
                  formBody.append("entry.203805592", mobile10 || "");

                  // ✅ Run BOTH together
                  const [formRes, apiRes] = await Promise.allSettled([
                    fetch(formUrl, {
                      method: "POST",
                      mode: "no-cors", // ✅ required for Google Forms from browser
                      headers: {
                        "Content-Type": "application/x-www-form-urlencoded",
                      },
                      body: formBody.toString(),
                    }),
                    api.delete(`/users/${userId}`, {
                      headers: { "x-auth-phone": dealer.mobile },
                    }),
                  ]);

                  // ✅ If API delete failed → show error (don't remove from UI)
                  if (apiRes.status === "rejected") {
                    throw apiRes.reason;
                  }

                  // ✅ Success: update UI
                  setUsers((prev) => prev.filter((x) => x._id !== userId));
                  toast.success("User deleted successfully", {
                    id: loadingToast,
                  });

                  // (optional) If you want to know if form call failed (cannot read response due to no-cors)
                  if (formRes.status === "rejected") {
                    console.warn("Google Form submit failed:", formRes.reason);
                  }
                } catch (err) {
                  toast.error(
                    err?.response?.data?.message || "Failed to delete user",
                    {
                      id: loadingToast,
                    },
                  );
                }
              }}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition"
            >
              Yes, Delete
            </button>
          </div>
        </div>
      ),
      {
        duration: Infinity,
        style: {
          maxWidth: "420px",
          padding: "16px",
          background: "#fff",
          color: "#333",
        },
      },
    );
  };

  const handleEdit = (user) => {
    if (onEditUser) return onEditUser(user);
    navigate("/add-user", { state: { user } });
  };

  const openUserDetailsModal = (user) => {
    setSelectedUser(resolveUser(user));
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
    setZoomedImage(null);
  };

  const openZoom = (imageUrl) => setZoomedImage(imageUrl);
  const closeZoom = () => setZoomedImage(null);

  const first = () => setPage(1);
  const prev = () => setPage((p) => Math.max(1, p - 1));
  const next = () => setPage((p) => Math.min(totalPages, p + 1));
  const last = () => setPage(totalPages);

  const pageButtons = useMemo(() => {
    const start = Math.max(1, page - 2);
    const end = Math.min(totalPages, page + 2);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [page, totalPages]);

  const PaginationBar = () =>
    totalPages > 1 ? (
      <div className="px-3 sm:px-4 py-2 sm:py-3 bg-white border-t flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs sm:text-sm text-gray-600">
          Showing{" "}
          <b className="text-gray-900">{(page - 1) * rowsPerPage + 1}</b>
          {" - "}
          <b className="text-gray-900">
            {Math.min(page * rowsPerPage, filteredUsers.length)}
          </b>{" "}
          of <b className="text-gray-900">{filteredUsers.length}</b>
        </div>

        <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
          <button
            onClick={first}
            disabled={page === 1}
            className="h-8 sm:h-9 px-2 sm:px-3 rounded-md sm:rounded-lg bg-gray-50 border hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition font-semibold text-xs sm:text-sm"
          >
            First
          </button>

          <button
            onClick={prev}
            disabled={page === 1}
            className="h-8 sm:h-9 px-2 sm:px-3 rounded-md sm:rounded-lg bg-gray-50 border hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition font-semibold text-xs sm:text-sm"
          >
            Prev
          </button>

          <div className="flex items-center gap-1">
            {pageButtons.map((p) => (
              <button
                key={p}
                onClick={() => setPage(p)}
                className={`h-8 w-8 sm:h-9 sm:w-9 rounded-md sm:rounded-lg border transition font-semibold text-xs sm:text-sm ${
                  p === page
                    ? "bg-yellow-600 text-white border-yellow-600"
                    : "bg-white hover:bg-gray-50"
                }`}
              >
                {p}
              </button>
            ))}
          </div>

          <button
            onClick={next}
            disabled={page === totalPages}
            className="h-8 sm:h-9 px-2 sm:px-3 rounded-md sm:rounded-lg bg-gray-50 border hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition font-semibold text-xs sm:text-sm"
          >
            Next
          </button>

          <button
            onClick={last}
            disabled={page === totalPages}
            className="h-8 sm:h-9 px-2 sm:px-3 rounded-md sm:rounded-lg bg-gray-50 border hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition font-semibold text-xs sm:text-sm"
          >
            Last
          </button>
        </div>
      </div>
    ) : null;

  return (
    <>
      <div className="bg-white rounded-2xl shadow-md overflow-hidden border p-4 sm:p-5">
        <div className="grid grid-cols-1 gap-3 sm:gap-6 mb-4">
          <div className="bg-yellow-200 border border-yellow-600 rounded-xl p-3 sm:p-4 flex flex-col items-center text-center hover:shadow-md transition">
            <h2 className="text-base sm:text-lg font-extrabold text-gray-900">
              My Customers
            </h2>
            <p className="text-lg sm:text-2xl font-bold text-yellow-700 mt-1">
              {filteredUsers.length}
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
            />
            <FaSearch
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              size={16}
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="p-6 sm:p-12 text-center text-gray-600 text-sm sm:text-lg">
          Loading users...
        </div>
      ) : users.length === 0 ? (
        <div className="p-6 sm:p-12 text-center text-gray-600 text-sm sm:text-lg">
          No users added yet.
          <br />
          Click <strong>"Add New User"</strong> to start!
        </div>
      ) : (
        <div className="px-3 pt-4 pb-6 sm:p-6">
          <div className="rounded-2xl border bg-white shadow-sm">
            {/* MOBILE CARDS */}
            <div className="sm:hidden px-3 pb-4 space-y-4 h-[50vh] py-2 overflow-y-auto">
              {currentRows.map((user) => {
                const ru = resolveUser(user);

                return (
                  <div
                    key={user._id}
                    className="bg-white border rounded-2xl px-4 pt-6 pb-4 shadow-sm transition hover:bg-yellow-50 active:scale-[0.98]"
                  >
                    <div
                      onClick={() => setQuickUser(user)}
                      className="flex items-center gap-4 mb-4 cursor-pointer"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="flex items-center gap-2 font-bold text-gray-900 text-base truncate hover:text-yellow-700 transition-colors">
                          <span className="truncate">{ru.name}</span>
                          <span className="flex items-center gap-1.5 text-gray-600 font-medium whitespace-nowrap">
                            <FaPhone
                              className="text-gray-400 flex-shrink-0"
                              size={14}
                            />
                            {ru.mobile || "—"}
                          </span>
                        </p>
                      </div>
                    </div>

                    <div className="pt-3 border-t flex justify-between items-center">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onAddSale?.(user);
                        }}
                        className="flex items-center gap-1.5 text-yellow-600 font-semibold text-sm hover:text-yellow-800"
                      >
                        <FaPlusCircle size={16} />
                        Sale
                      </button>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(user);
                        }}
                        className="flex items-center gap-1.5 text-blue-600 font-semibold text-sm hover:text-blue-800"
                      >
                        <FaEdit size={16} />
                        Edit
                      </button>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openUserDetailsModal(user);
                        }}
                        className="flex items-center gap-1.5 text-green-600 font-semibold text-sm hover:text-green-800"
                      >
                        <FaEye size={16} />
                        View
                      </button>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(user._id, user.name);
                        }}
                        className="flex items-center gap-1.5 text-red-600 font-semibold text-sm hover:text-red-800"
                      >
                        <FaTrash size={16} />
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="lg:hidden">
              <PaginationBar />
            </div>

            {/* DESKTOP TABLE */}
            <div
              className="hidden sm:block overflow-auto"
              style={{ height: "420px" }}
            >
              <table className="w-full text-sm min-w-[980px]">
                <thead className="bg-gray-50 sticky top-0 z-10 border-b">
                  <tr>
                    <th className="text-left px-5 py-3 font-semibold text-gray-700 whitespace-nowrap">
                      Profile
                    </th>
                    <th className="text-left px-5 py-3 font-semibold text-gray-700 whitespace-nowrap">
                      Name
                    </th>
                    <th className="text-left px-5 py-3 font-semibold text-gray-700 whitespace-nowrap">
                      Mobile
                    </th>
                    <th className="text-left px-5 py-3 font-semibold text-gray-700 whitespace-nowrap">
                      Type
                    </th>
                    <th className="text-left px-5 py-3 font-semibold text-gray-700 whitespace-nowrap">
                      Area
                    </th>
                    <th className="text-left px-5 py-3 font-semibold text-gray-700 whitespace-nowrap">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200">
                  {currentRows.map((user) => {
                    const ru = resolveUser(user);
                    const { percent, color, pending } =
                      getProfileCompletion(user);

                    return (
                      <tr
                        key={user._id}
                        onClick={() => setQuickUser(user)}
                        className="hover:bg-yellow-50 transition cursor-pointer"
                      >
                        <td className="px-5 py-3">
                          <CircularProfile
                            percent={pending ? 0 : percent}
                            color={color}
                            image={ru.profilePic}
                            name={ru.name}
                            pending={pending}
                          />
                        </td>
                        <td className="px-5 py-3 font-semibold text-gray-900 whitespace-nowrap">
                          {ru.name}
                        </td>
                        <td className="px-5 py-3 text-gray-700 whitespace-nowrap">
                          {ru.mobile}
                        </td>
                        <td className="px-5 py-3 text-gray-600 whitespace-nowrap">
                          {ru.type || "—"}
                        </td>
                        <td className="px-5 py-3 text-gray-600 whitespace-nowrap">
                          {ru.area || "—"}
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-4">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onAddSale?.(user);
                              }}
                              className="flex items-center gap-2 text-yellow-600 font-semibold text-sm md:text-base hover:text-yellow-800 px-3 py-2 rounded-lg transition"
                            >
                              <FaPlusCircle size={16} />
                              Sale
                            </button>

                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(user);
                              }}
                              className="flex items-center gap-2 text-blue-600 font-semibold text-sm md:text-base hover:text-blue-800 px-3 py-2 rounded-lg transition"
                            >
                              <FaEdit size={16} />
                              Edit
                            </button>

                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openUserDetailsModal(user);
                              }}
                              className="flex items-center gap-2 text-green-600 font-semibold text-sm md:text-base hover:text-green-800 px-3 py-2 rounded-lg transition"
                            >
                              <FaEye size={16} />
                              View
                            </button>

                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(user._id, user.name);
                              }}
                              className="flex items-center gap-2 text-red-600 font-semibold text-sm md:text-base hover:text-red-800 px-3 py-2 rounded-lg transition"
                            >
                              <FaTrash size={16} />
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="hidden sm:block">
              <PaginationBar />
            </div>
          </div>
        </div>
      )}

      {/* QUICK USER POPUP */}
      {quickUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setQuickUser(null)}
          />
          <div className="relative w-full max-w-sm bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <div className="flex justify-between items-start">
              <div className="min-w-0">
                <p className="font-bold text-lg text-gray-900 truncate">
                  {quickUser.name} ({quickUser.mobile})
                </p>
                <p className="text-xs text-gray-500 mt-0.5 truncate">
                  {quickUser.area || "—"}
                </p>
              </div>

              <button
                onClick={() => setQuickUser(null)}
                className="h-8 w-8 rounded-lg hover:bg-gray-200 grid place-items-center transition"
                aria-label="Close"
              >
                <FaTimes className="text-gray-600" />
              </button>
            </div>

            <div className="border-t border-gray-100 my-4" />

            <button
              onClick={() => {
                onAddSale?.(quickUser);
                setQuickUser(null);
              }}
              className="mt-5 w-full h-11 bg-yellow-600 text-white rounded-xl font-bold hover:bg-yellow-700 transition flex items-center justify-center gap-2"
            >
              <FaPlusCircle />
              Add Sale
            </button>
          </div>
        </div>
      )}

      {/* USER DETAILS MODAL */}
      {isModalOpen && selectedUser && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-2 sm:p-4">
          <div className="bg-white w-full sm:max-w-5xl rounded-2xl shadow-2xl overflow-hidden max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b">
              <h3 className="text-base sm:text-xl font-extrabold flex items-center gap-2">
                <FaUser className="text-yellow-600" />
                Customer Profile & Documents
              </h3>

              <button
                onClick={closeModal}
                className="h-9 w-9 rounded-lg grid place-items-center hover:bg-gray-100 active:scale-95 transition"
                aria-label="Close modal"
              >
                <FaTimes className="text-xl" />
              </button>
            </div>

            <div className="p-3 sm:p-6 space-y-4 sm:space-y-6 overflow-y-auto flex-1">
              <div className="border rounded-xl sm:rounded-2xl p-3 sm:p-5">
                <div className="flex items-start sm:items-center gap-3 sm:gap-4">
                  {selectedUser.profilePic ? (
                    isDriveViewLink(selectedUser.profilePic) ? (
                      <iframe
                        src={driveViewToPreview(selectedUser.profilePic)}
                        title="profile-preview-modal"
                        className="w-14 h-14 sm:w-20 sm:h-20 rounded-xl border"
                        allow="autoplay"
                      />
                    ) : (
                      <img
                        src={selectedUser.profilePic}
                        onClick={() => openZoom(selectedUser.profilePic)}
                        className="w-14 h-14 sm:w-20 sm:h-20 rounded-xl object-cover border cursor-pointer"
                        alt=""
                        onError={(e) =>
                          (e.currentTarget.style.display = "none")
                        }
                      />
                    )
                  ) : (
                    <div className="w-14 h-14 sm:w-20 sm:h-20 rounded-xl bg-yellow-200 flex items-center justify-center text-xl sm:text-3xl font-bold text-yellow-800">
                      {selectedUser.name?.charAt(0)?.toUpperCase() || "U"}
                    </div>
                  )}

                  <div className="min-w-0">
                    <p className="text-base sm:text-xl font-extrabold text-gray-900 truncate">
                      {selectedUser.name}
                    </p>
                    <p className="text-sm sm:text-base text-gray-600 flex items-center gap-2 mt-1">
                      <FaPhone /> {selectedUser.mobile}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mt-4 sm:mt-5">
                  <div className="border rounded-xl p-3 sm:p-4 flex items-center gap-3">
                    <FaIdCard className="text-yellow-600 text-lg sm:text-xl" />
                    <div>
                      <p className="text-[11px] sm:text-xs text-gray-500">
                        Customer Type
                      </p>
                      <p className="text-sm sm:text-base font-semibold text-gray-900">
                        {selectedUser.type || "—"}
                      </p>
                    </div>
                  </div>

                  <div className="border rounded-xl p-3 sm:p-4 flex items-center gap-3">
                    <FaMapMarkerAlt className="text-yellow-600 text-lg sm:text-xl" />
                    <div className="min-w-0">
                      <p className="text-[11px] sm:text-xs text-gray-500">
                        Location
                      </p>
                      <p className="text-sm sm:text-base font-semibold text-gray-900 truncate">
                        {selectedUser.district || "—"},{" "}
                        {selectedUser.area || "—"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="border rounded-xl sm:rounded-2xl p-3 sm:p-5">
                <div className="flex justify-between items-center mb-3 sm:mb-4">
                  <h4 className="text-base sm:text-lg font-extrabold text-gray-900">
                    Documents
                  </h4>
                  <span className="text-[11px] sm:text-xs text-gray-500">
                    (Tap to zoom)
                  </span>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  {[
                    { key: "pan", label: "PAN" },
                    { key: "aadhar", label: "Aadhaar" },
                  ].map((doc) => {
                    const url = selectedUser.documents?.[doc.key] || null;

                    return (
                      <div
                        key={doc.key}
                        className="border rounded-xl p-3 sm:p-4"
                      >
                        <div className="flex justify-between items-center mb-2">
                          <p className="text-sm sm:text-base font-semibold">
                            {doc.label}
                          </p>

                          <span
                            className={`text-[10px] sm:text-xs px-2 py-1 rounded-full ${
                              url
                                ? "bg-green-100 text-green-700"
                                : "bg-gray-100 text-gray-500"
                            }`}
                          >
                            {url ? "Uploaded" : "Missing"}
                          </span>
                        </div>

                        {url ? (
                          isDriveViewLink(url) ? (
                            <iframe
                              src={driveViewToPreview(url)}
                              title={`${doc.label}-preview`}
                              className="w-full h-32 sm:h-40 rounded-lg border"
                              allow="autoplay"
                            />
                          ) : (
                            <img
                              src={url}
                              onClick={() => openZoom(url)}
                              className="w-full h-24 sm:h-32 object-cover rounded-lg border cursor-pointer"
                              alt=""
                              onError={(e) =>
                                (e.currentTarget.style.display = "none")
                              }
                            />
                          )
                        ) : (
                          <div className="w-full h-24 sm:h-32 flex items-center justify-center bg-gray-50 rounded-lg text-xs sm:text-sm text-gray-500 italic">
                            Not uploaded
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="border-t px-3 sm:px-6 py-3 sm:py-4 flex justify-end">
              <button
                onClick={closeModal}
                className="h-10 sm:h-auto px-4 sm:px-6 py-2 bg-yellow-600 text-white rounded-xl font-semibold hover:bg-yellow-700 active:scale-95 transition text-sm sm:text-base"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ZOOM OVERLAY (only for real images, not iframe preview) */}
      {zoomedImage && (
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-[100] p-6"
          onClick={() => setZoomedImage(null)}
        >
          <button
            onClick={() => setZoomedImage(null)}
            className="absolute top-6 right-6 text-white text-4xl hover:text-gray-300 transition"
          >
            <FaTimes />
          </button>

          <img
            src={zoomedImage}
            alt="Zoomed"
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}
